import { Block, BLOCKS, Document, helpers, Inline, INLINES, Mark, MARKS, Text } from '@contentful/rich-text-types';
import escape from 'escape-html';

const attributeValue = (value: string) => `"${value.replace(/"/g, '&quot;')}"`;

const defaultNodeRenderers: RenderNode = {
  [BLOCKS.PARAGRAPH]: (node, next) => `<p>${next(node.content)}</p>`,
  [BLOCKS.HEADING_1]: (node, next) => `<h1>${next(node.content)}</h1>`,
  [BLOCKS.HEADING_2]: (node, next) => `<h2>${next(node.content)}</h2>`,
  [BLOCKS.HEADING_3]: (node, next) => `<h3>${next(node.content)}</h3>`,
  [BLOCKS.HEADING_4]: (node, next) => `<h4>${next(node.content)}</h4>`,
  [BLOCKS.HEADING_5]: (node, next) => `<h5>${next(node.content)}</h5>`,
  [BLOCKS.HEADING_6]: (node, next) => `<h6>${next(node.content)}</h6>`,
  [BLOCKS.EMBEDDED_ENTRY]: (node, next) => `<div>${next(node.content)}</div>`,
  [BLOCKS.UL_LIST]: (node, next) => `<ul>${next(node.content)}</ul>`,
  [BLOCKS.OL_LIST]: (node, next) => `<ol>${next(node.content)}</ol>`,
  [BLOCKS.LIST_ITEM]: (node, next) => `<li>${next(node.content)}</li>`,
  [BLOCKS.QUOTE]: (node, next) => `<blockquote>${next(node.content)}</blockquote>`,
  [BLOCKS.HR]: () => '<hr/>',
  [BLOCKS.TABLE]: (node, next) => `<table>${next(node.content)}</table>`,
  [BLOCKS.TABLE_ROW]: (node, next) => `<tr>${next(node.content)}</tr>`,
  [BLOCKS.TABLE_HEADER_CELL]: (node, next) => `<th>${next(node.content)}</th>`,
  [BLOCKS.TABLE_CELL]: (node, next) => `<td>${next(node.content)}</td>`,
  [INLINES.ASSET_HYPERLINK]: (node) => `<p class="fa fa-file-code blue-xml-file-view-icon xmlModal" title = https:${node.data['target'].fields.file.url}></p>`,
  [INLINES.ENTRY_HYPERLINK]: (node) => defaultInline(INLINES.ENTRY_HYPERLINK, node as Inline),
  [INLINES.EMBEDDED_ENTRY]: (node) => {
    switch (node.data['target'].fields.category || '') {
      case 'revision':
        return `<span class="${node.data['target'].fields.iconClass}" >${node.data['target'].fields.title.split('#').pop()}</span>`;
      case 'note-in-one-line':
        return `<div class="notice info-panel mt-10 mb-10">
                  <i class="${node.data['target'].fields.iconClass}"></i>
                  <span>${node.data['target'].fields.title.split('#').pop()} : ${node.data['target'].fields.body}</span>
                </div>`;
      case 'note-with-points':
        return `<div class="alert info-panel mt-10 mb-10" role="alert">
                  <i class="${node.data['target'].fields.iconClass}"></i><strong>${node.data['target'].fields.title.split('#').pop()}</strong>
                  <span>${node.data['target'].fields.body}</span>
                </div>`;
      case 'yellow-note-in-one-line':
        return `<div class="warning-note mt-10 mb-10">
                  <i class="${node.data['target'].fields.iconClass}"></i>
                    <span>${node.data['target'].fields.title.split('#').pop()} : ${node.data['target'].fields.body}</span>
                </div>`;
      case 'yellow-note-with-points':
        return `<div class="alert warning-note mt-10 mb-10" role="alert">
                    <i class="${node.data['target'].fields.iconClass}"></i><strong>${node.data['target'].fields.title.split('#').pop()}</strong>
                    <span>${node.data['target'].fields.body}</span>
                </div>`;
      default:
        return defaultInline(INLINES.ENTRY_HYPERLINK, node as Inline);
    }
  },
  [INLINES.HYPERLINK]: (node, next) => {
    const href = typeof node.data['uri'] === 'string' ? node.data['uri'] : '';
    return `<a href=${attributeValue(href)}>${next(node.content)}</a>`;
  }
};

const defaultMarkRenderers: RenderMark = {
  [MARKS.BOLD]: (text) => `<b>${text}</b>`,
  [MARKS.ITALIC]: (text) => `<i>${text}</i>`,
  [MARKS.UNDERLINE]: (text) => `<u>${text}</u>`,
  [MARKS.CODE]: (text) => {
    if (text.includes('#ABBR#')) {
      return `<span class="ontooltip">${text.split('#ABBR#')[0]}<span class="tooltipContent">${text.split('#ABBR#')[1]}</span></span>`;
    } else {
      return `<code>${text}</code>`;
    }
  }
};

const defaultInline = (type: string, node: Inline) => `<span>type: ${escape(type)} id: ${escape(node.data['target'].sys.id)}</span>`;

export type CommonNode = Text | Block | Inline;

export interface Next {
  (nodes: CommonNode[]): string;
}

export interface NodeRenderer {
  (node: Block | Inline, next: Next): string;
}

export interface RenderNode {
  [k: string]: NodeRenderer;
}

export interface RenderMark {
  [k: string]: (text: string) => string;
}

export interface Options {
  renderNode?: RenderNode;
  renderMark?: RenderMark;
}

export function documentToHtmlString(richTextDocument: Document, options: Partial<Options> = {}): string {
  if (!richTextDocument || !richTextDocument.content) {
    return '';
  }

  let value = nodeListToHtmlString(richTextDocument.content, {
    renderNode: {
      ...defaultNodeRenderers,
      ...options.renderNode
    },
    renderMark: {
      ...defaultMarkRenderers,
      ...options.renderMark
    }
  });
  return value;
}

function nodeListToHtmlString(nodes: CommonNode[], { renderNode, renderMark }: Options): string {
  const value = nodes.map<string>((node) => nodeToHtmlString(node, { renderNode, renderMark })).join('');
  return value;
}

function nodeToHtmlString(node: CommonNode, { renderNode, renderMark }: Options): string {
  if (helpers.isText(node)) {
    const nodeValue = escape(node.value);
    if (node.marks.length > 0) {
      return node.marks.reduce((value: string, mark: Mark) => {
        if (renderMark && !renderMark[mark.type]) {
          return value;
        }
        return renderMark ? renderMark[mark.type](value) : '';
      }, nodeValue);
    }

    return nodeValue;
  } else {
    const nextNode: Next = (nodes) => nodeListToHtmlString(nodes, { renderMark, renderNode });
    if (!node.nodeType || (renderNode && !renderNode[node.nodeType])) {
      return '';
    }
    let nodeValue = renderNode ? renderNode[node.nodeType](node, nextNode) : '';
    return nodeValue;
  }
}
